import './ThesisDetail.css';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PerspectiveInfoSheet from './PerspectiveInfoSheet.jsx';
import vorData from './vorData.json';
import { THESIS_BARO_DATA, TOPIC_LABELS } from './thesisBaroData';

const DAY = 86400000;
// "Today" anchors the right edge of the default view.
const TODAY_MS = Date.parse('2026-07-13');

// Townhall stations shown on the top timeline, at their real dates.
const STATIONS = [
  { id: 'msc',    ms: Date.parse('2026-02-12'), flagship: true },
  { id: 'paris',  ms: Date.parse('2026-05-29')                 },
  { id: 'oxford', ms: Date.parse('2026-06-23'), heute: true    },
];

// Legacy 2026 series/events are authored in a station-indexed `t` coordinate
// (t=0 MSC · t=1 Paris · t=2 Oxford). Convert it to an absolute date so the
// whole chart can live on one continuous, uniform time axis.
const MSC_2026_MS = Date.parse('2026-02-12');
function tToDate(t) {
  // Piecewise-linear in days: 106 days from MSC→Paris, 25 from Paris→Oxford.
  if (t <= 1) return MSC_2026_MS + t * 106 * DAY;
  return MSC_2026_MS + (106 + (t - 1) * 25) * DAY;
}

// Snap a date to the first of its month (used to place the top-timeline
// townhall markers on clean month boundaries for the demo).
const roundToMonth = (ms) => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

// Linear interpolation of a {ms, v}[] series (sorted) at an arbitrary date.
function valueAt(series, ms) {
  if (!series.length) return 0;
  if (ms <= series[0].ms) return series[0].v;
  const last = series[series.length - 1];
  if (ms >= last.ms) return last.v;
  for (let i = 1; i < series.length; i++) {
    if (ms <= series[i].ms) {
      const a = series[i - 1], b = series[i];
      const f = (ms - a.ms) / (b.ms - a.ms);
      return a.v + f * (b.v - a.v);
    }
  }
  return last.v;
}

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const Y_MIN = 0;
const Y_MAX = 100;

// How many days of history fit across the plot width at once (≈ 6 months).
const VISIBLE_DAYS = 183;
const HEAD_TAIL = 14 * DAY;   // small lead-in / lead-out so lines don't start abruptly

// Build one continuous {ms, v}[] history for a cohort: optional 2025 data +
// the untouched 2026 `t` series, with short tails on each end.
function cohortDateSeries(c) {
  const pts2026 = c.series.filter(p => p.t <= 2).map(p => ({ ms: tToDate(p.t), v: p.v }));
  const pre2025 = (c.series2025 || []).map(p => ({ ms: Date.parse(p.date), v: p.v }));
  const merged  = [...pre2025, ...pts2026].sort((a, b) => a.ms - b.ms);
  if (!merged.length) return [];
  const headV = pre2025.length ? merged[0].v : (c.preV ?? merged[0].v);
  const postV = c.postV ?? merged[merged.length - 1].v;
  const head  = { ms: merged[0].ms - HEAD_TAIL, v: headV };
  const tail  = { ms: Math.max(merged[merged.length - 1].ms + HEAD_TAIL, TODAY_MS), v: postV };
  return [head, ...merged, tail];
}

export default function ThesisDetail() {
  const history     = useHistory();
  const { id }      = useParams();
  const { t, i18n } = useTranslation();
  const lang        = i18n.language?.slice(0, 2) || 'en';

  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeGlobal, setActiveGlobal] = useState(null);
  const lblTextRefs = useRef({});
  const [lblTextWidths, setLblTextWidths] = useState({});

  useEffect(() => { setActiveGlobal(null); }, [id]);

  useEffect(() => {
    const widths = {};
    for (const [refId, el] of Object.entries(lblTextRefs.current)) {
      if (el) widths[refId] = el.getBBox().width;
    }
    // Only update when a value actually changed; returning the previous state
    // reference lets React bail out and avoids an infinite render loop.
    setLblTextWidths(prev => {
      const keys = Object.keys(widths);
      const unchanged =
        keys.length === Object.keys(prev).length &&
        keys.every(k => prev[k] === widths[k]);
      return unchanged ? prev : widths;
    });
  });

  const [isLandscape, setIsLandscape] = useState(
    () => typeof window !== 'undefined' && window.innerWidth > window.innerHeight && window.innerWidth >= 900
  );

  const wrapRef           = useRef(null);
  const [wrapW, setWrapW] = useState(360);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => { if (el) setWrapW(el.offsetWidth || 360); });
    obs.observe(el);
    setWrapW(el.offsetWidth || 360);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const check = () => setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth >= 900);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const isTablet = wrapW >= 480;

  // ── Dynamic thesis data ───────────────────────────────────────────────────
  const langKey     = ['de', 'fr'].includes(lang) ? lang : 'en';
  const thesisVor   = vorData.theses.find(th => th.id === id);
  const thesisData  = THESIS_BARO_DATA[id] || THESIS_BARO_DATA['t2'];
  const COHORTS     = thesisData.cohorts;
  const GLOBAL_EVENTS = thesisData.globalEvents;

  const topicId     = thesisVor?.topicId || 'transatlantic';
  const topicLabel  = TOPIC_LABELS[topicId]?.[langKey] || TOPIC_LABELS[topicId]?.en || t('td.eyebrow');
  const topicTheses = vorData.theses.filter(th => th.topicId === topicId);
  const thesisNum   = Math.max(1, topicTheses.findIndex(th => th.id === id) + 1);
  const thesisText  = lang === 'de' ? thesisVor?.text_de
                    : lang === 'fr' ? thesisVor?.text_fr
                    : (thesisVor?.text_en || t('td.baroThesis'));

  const findCohort  = (cohortId) => COHORTS.find(c => c.id === cohortId);
  const fullLabel   = (cohortId) => {
    const c = findCohort(cohortId);
    return c?.label?.[langKey] || c?.label?.en || cohortId;
  };
  const colorOf     = (cohortId) => findCohort(cohortId)?.color || 'rgba(248,247,245,0.85)';

  // ── SVG geometry ─────────────────────────────────────────────────────────────
  // Symmetric margins: perspectives are named in a legend (not the right
  // gutter), so the plotted lines get near-full width with balanced padding.
  const X_LEFT   = isTablet ? 56 : 46;
  const X_RIGHT  = isTablet ? 56 : 46;
  const HEADER_H = isTablet ? 52  : 44;
  const PT       = isTablet ? 12  : 8;
  const PB       = isTablet ? 4   : 4;
  const svgH     = isTablet ? (isLandscape ? 340 : 560) : 420;
  const plotH    = svgH - HEADER_H - PT - PB;
  const plotW    = Math.max(100, wrapW);

  const plotLeft  = X_LEFT;
  const plotRight = plotW - X_RIGHT;
  const plotAreaW = Math.max(50, plotRight - plotLeft);
  const PPD       = plotAreaW / VISIBLE_DAYS;          // pixels per day (uniform)
  const viewLeft0 = TODAY_MS - VISIBLE_DAYS * DAY;     // date at plotLeft when pan = 0

  // Stations: global 2026 townhalls + any thesis-specific earlier stations.
  // Positions are rounded to month boundaries so the top-timeline markers sit
  // at clean month spacing (and the close townhalls stop overlapping).
  const stations2025 = (thesisData.stations2025 || []).map(s => ({ ...s, ms: Date.parse(s.dateISO) }));
  const allStations = [...stations2025, ...STATIONS]
    .map(s => ({ ...s, ms: roundToMonth(s.ms) }))
    .sort((a, b) => a.ms - b.ms);
  const evDate = (ev) => (ev.dateISO ? Date.parse(ev.dateISO) : tToDate(ev.t));

  // Precompute continuous per-cohort histories + overall data extent.
  const cohortSeries = COHORTS.map(c => ({ id: c.id, color: c.color, pts: cohortDateSeries(c) }));
  const allMs = [
    ...cohortSeries.flatMap(cs => cs.pts.map(p => p.ms)),
    ...GLOBAL_EVENTS.map(evDate),
  ];
  const dataMinMs = allMs.length ? Math.min(...allMs) : viewLeft0;

  // Pan is measured in pixels: 0 = default (today at right edge),
  // increasing shifts content right to reveal the past.
  const panMaxPx = Math.max(0, PPD * (viewLeft0 - dataMinMs) / DAY);
  const [panPx, setPanPx] = useState(0);
  const pan = clamp(panPx, 0, panMaxPx);

  const panPxRef  = useRef(0);
  const panMaxRef = useRef(0);
  const stepRef   = useRef(0);
  panPxRef.current  = pan;
  panMaxRef.current = panMaxPx;
  stepRef.current   = plotAreaW * 0.62;

  const xAt = (ms) => plotLeft + PPD * (ms - viewLeft0) / DAY + pan;
  const yAt = (v)  => HEADER_H + PT + (1 - (v - Y_MIN) / (Y_MAX - Y_MIN)) * plotH;

  // Date currently sitting at the right edge of the plot (drives live labels).
  const rightEdgeMs = TODAY_MS - (pan / PPD) * DAY;

  // ── Pan interaction (drag + momentum + arrow steps) ───────────────────────
  const rafRef   = useRef(null);
  const dragRef  = useRef(null);
  const moveRef  = useRef(null);
  const upRef    = useRef(null);
  const suppressClickRef = useRef(false);

  const cancelAnim = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const animateTo = useCallback((to, dur = 420) => {
    cancelAnim();
    const from = panPxRef.current;
    const target = clamp(to, 0, panMaxRef.current);
    if (Math.abs(target - from) < 0.5) { setPanPx(target); return; }
    const start = performance.now();
    const tick = () => {
      const p = Math.min(1, (performance.now() - start) / dur);
      const e = 1 - Math.pow(1 - p, 3);   // easeOutCubic
      setPanPx(from + (target - from) * e);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [cancelAnim]);

  // Stable window-level move/up handlers (read live values from refs).
  useEffect(() => {
    moveRef.current = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const now = performance.now();
      const nx = clamp(d.startPan + (e.clientX - d.startX), 0, panMaxRef.current);
      if (Math.abs(e.clientX - d.startX) > 3) d.moved = true;
      const dt = now - d.lastT;
      if (dt > 0) d.vel = (e.clientX - d.lastX) / dt;   // px per ms
      d.lastX = e.clientX; d.lastT = now;
      setPanPx(nx);
    };
    upRef.current = () => {
      const d = dragRef.current;
      window.removeEventListener('pointermove', moveRef.current);
      window.removeEventListener('pointerup', upRef.current);
      dragRef.current = null;
      if (!d) return;
      suppressClickRef.current = d.moved;
      setTimeout(() => { suppressClickRef.current = false; }, 0);
      if (d.moved) animateTo(panPxRef.current + (d.vel || 0) * 130, 520);
    };
    return () => {
      window.removeEventListener('pointermove', moveRef.current);
      window.removeEventListener('pointerup', upRef.current);
    };
  }, [animateTo]);

  useEffect(() => () => cancelAnim(), [cancelAnim]);

  const onPointerDown = (e) => {
    if (panMaxRef.current <= 0) return;   // nothing to navigate
    cancelAnim();
    dragRef.current = {
      startX: e.clientX, startPan: panPxRef.current,
      lastX: e.clientX, lastT: performance.now(), vel: 0, moved: false,
    };
    window.addEventListener('pointermove', moveRef.current);
    window.addEventListener('pointerup', upRef.current);
  };

  const stepBy = (dir) => animateTo(panPxRef.current + dir * stepRef.current, 380);
  const canPast    = pan < panMaxPx - 1;   // more history to the left
  const canPresent = pan > 1;              // can return toward today

  const dotR    = isTablet ? 7   : 4;
  const strokeW = isTablet ? 5   : 2.5;
  const axisFsz = isTablet ? 10  : 9;

  // Column band width, clamped so tightly-spaced stations don't overlap.
  const stationGapsPx = allStations.slice(1).map((s, i) => (s.ms - allStations[i].ms) / DAY * PPD);
  const minStationGap = stationGapsPx.length ? Math.min(...stationGapsPx) : 40;
  const COL_W = Math.min(isTablet ? 32 : 50, Math.max(20, minStationGap - 8));

  // Global-events lane geometry
  const GE_AXIS_Y = 6;
  const GE_STEM_1 = 15;
  const GE_STEM_2 = 32;
  const GE_HEIGHT = isTablet ? 56 : 52;
  const hdrNameFsz = isTablet ? 13 : 11;
  const hdrSubFsz  = isTablet ? 11 : 9;

  const activeGlobalEv = GLOBAL_EVENTS.find(e => e.id === activeGlobal) ?? null;

  // ── Live cohort labels: value at the right edge, collision-adjusted ────────
  const fsz    = isTablet ? 12 : 10;
  const padH   = isTablet ? 6  : 5;
  const padV   = isTablet ? 3  : 2;
  const badgeH = fsz + 2 * padV;
  const lblGap = badgeH + (isTablet ? 3 : 2);

  const edgeVal = (id) => valueAt(cohortSeries.find(cs => cs.id === id).pts, rightEdgeMs);

  // Badges sit just ABOVE each line's right-edge point and are centred over it
  // horizontally (the symmetric right margin gives them room to overhang). Where
  // line ends bunch together, they are nudged upward into the open space above.
  const gapAbove = badgeH / 2 + 7;
  const lblItems = COHORTS.map((c) => ({
    id: c.id, color: c.color, label: fullLabel(c.id), y: yAt(edgeVal(c.id)) - gapAbove,
  })).sort((a, b) => b.y - a.y);   // lowest line first, then climb upward

  for (let i = 1; i < lblItems.length; i++) {
    if (lblItems[i - 1].y - lblItems[i].y < lblGap) {
      lblItems[i].y = lblItems[i - 1].y - lblGap;
    }
  }
  const lblTop = HEADER_H + badgeH / 2 + 2;
  lblItems.forEach((it) => { it.y = Math.max(it.y, lblTop); });

  const clipId   = 'td-clip-chart';
  const clipIdGe = 'td-clip-ge';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="td td-page">

      <div className="td-body">

        <button className="td-back" onClick={() => history.goBack()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 3L5 8l5 5" />
          </svg>
          {t('td.back')}
        </button>

        {/* Thesis statement */}
        <div className="td-statement-block">
          <span className="td-topic-label">{topicLabel}</span>
          <p className="td-statement">
            <span className="td-thesis-arc" aria-hidden="true">
              <svg viewBox="5.3 5.3 29.4 29.4" className="td-thesis-arc-svg">
                <path d="M 32.95 21.13 A 13 13 0 1 1 20 7" stroke="#3B7CB8" strokeWidth="3.4" fill="none" strokeLinecap="round" />
                <text
                  x="20" y="20"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="#3B7CB8"
                  fontSize="17"
                  fontWeight="600"
                  fontFamily="'DM Sans', sans-serif"
                >{thesisNum}</text>
              </svg>
            </span>
            {thesisText}
          </p>
          <div className="td-meta-row">
            <span className="td-panel-size">{t('td.panelSize')}</span>
            <span className="td-demo-badge">{t('td.demo')}</span>
          </div>
        </div>

        {/* Chart */}
        <div
          ref={wrapRef}
          className="td-chart-wrap"
          onPointerDown={onPointerDown}
          style={{ touchAction: 'pan-y', position: 'relative' }}
        >
          <svg
            width="100%"
            height={svgH}
            style={{ display: 'block', overflow: 'visible' }}
            aria-hidden
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={plotLeft} y={0} width={plotAreaW} height={svgH} />
              </clipPath>
            </defs>

            {/* Pannable chart content, clipped to the plot window */}
            <g clipPath={`url(#${clipId})`}>
              {/* ── Column bands ── */}
              {allStations.map((s) => (
                <rect
                  key={s.id}
                  x={xAt(s.ms) - COL_W / 2}
                  y={HEADER_H}
                  width={COL_W}
                  height={PT + plotH}
                  fill={s.heute ? 'rgba(134,203,241,0.065)' : 'rgba(134,203,241,0.025)'}
                  rx={4}
                />
              ))}

              {/* ── Column headers ── */}
              {allStations.map((s) => {
                const nameY = isTablet ? 20 : 17;
                const subY  = isTablet ? 38 : 32;
                const name  = s.label ?? t(`td.station.${s.id}`);
                const sub   = s.sub ?? t(`td.station.${s.id}.sub`);
                return (
                  <g key={s.id}>
                    <text
                      x={xAt(s.ms)} y={nameY}
                      textAnchor="middle" dominantBaseline="middle"
                      fill={s.heute ? '#D3ECFC' : 'rgba(134,203,241,0.52)'}
                      fontSize={hdrNameFsz}
                      fontWeight={s.heute ? '700' : '500'}
                      fontFamily="'DM Sans', sans-serif"
                    >{name}</text>
                    <text
                      x={xAt(s.ms)} y={subY}
                      textAnchor="middle" dominantBaseline="middle"
                      fill={s.heute ? '#D4A244' : 'rgba(134,203,241,0.30)'}
                      fontSize={hdrSubFsz}
                      fontWeight={s.heute ? '500' : '400'}
                      fontFamily="'DM Sans', sans-serif"
                    >
                      {s.heute ? `${sub} · ${t('td.heute')}` : sub}
                    </text>
                  </g>
                );
              })}

              {/* ── Lines per cohort ── */}
              {cohortSeries.map((cs) => (
                <g key={cs.id}>
                  <polyline
                    points={cs.pts.map(p => `${xAt(p.ms)},${yAt(p.v)}`).join(' ')}
                    fill="none"
                    stroke={cs.color}
                    strokeWidth={strokeW}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {allStations.map((s) => (
                    <circle
                      key={s.id}
                      cx={xAt(s.ms)}
                      cy={yAt(valueAt(cs.pts, s.ms))}
                      r={dotR}
                      fill={cs.color}
                    />
                  ))}
                </g>
              ))}
            </g>

            {/* ── Y-axis labels (pinned, outside the clip) ── */}
            {[
              { v: 88, lines: ['Strongly', 'Agree']    },
              { v: 12, lines: ['Strongly', 'Disagree'] },
            ].map(({ v, lines }) => (
              <text
                key={v}
                x={0}
                textAnchor="start"
                fill="rgba(134,203,241,0.32)"
                fontSize={axisFsz}
                fontWeight="400"
                fontFamily="'DM Sans', sans-serif"
              >
                <tspan x={0} y={yAt(v) - axisFsz * 0.7}>{lines[0]}</tspan>
                <tspan x={0} y={yAt(v) + axisFsz * 0.7}>{lines[1]}</tspan>
              </text>
            ))}

            {/* ── Cohort labels: above each line's end, centred over it ── */}
            {lblItems.map((item) => {
              const measuredW = lblTextWidths[item.id];
              const bw = measuredW != null ? measuredW + 2 * padH : 0;
              const cx = plotRight;
              return (
                <g key={item.id}>
                  {measuredW != null && (
                    <rect
                      x={cx - bw / 2}
                      y={item.y - badgeH / 2}
                      width={bw}
                      height={badgeH}
                      rx={3}
                      ry={3}
                      fill={item.color}
                      fillOpacity={0.9}
                    />
                  )}
                  <text
                    ref={el => { lblTextRefs.current[item.id] = el; }}
                    x={cx}
                    y={item.y}
                    textAnchor="middle"
                    dominantBaseline="auto"
                    dy="0.35em"
                    fill="white"
                    fontSize={fsz}
                    fontWeight="600"
                    fontFamily="'DM Sans', sans-serif"
                  >
                    {item.label}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Navigation arrows — absolute overlay; never affect graph size */}
          <button
            className={`td-nav-arrow td-nav-arrow--left${canPast ? '' : ' td-nav-arrow--hidden'}`}
            style={{ top: HEADER_H + PT + plotH / 2 }}
            onClick={() => stepBy(1)}
            aria-label="Earlier"
            tabIndex={canPast ? 0 : -1}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 3L5 8l5 5" />
            </svg>
          </button>
          <button
            className={`td-nav-arrow td-nav-arrow--right${canPresent ? '' : ' td-nav-arrow--hidden'}`}
            style={{ top: HEADER_H + PT + plotH / 2 }}
            onClick={() => stepBy(-1)}
            aria-label="Later"
            tabIndex={canPresent ? 0 : -1}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 3l5 5-5 5" />
            </svg>
          </button>

          {/* ── Global events lane ── */}
          <svg
            width="100%"
            height={GE_HEIGHT}
            style={{ display: 'block', marginTop: 2, overflow: 'visible' }}
            aria-hidden
          >
            <defs>
              <clipPath id={clipIdGe}>
                <rect x={plotLeft} y={0} width={plotAreaW} height={GE_HEIGHT} />
              </clipPath>
            </defs>
            {/* Track line spanning full SVG width */}
            <line
              x1={0} y1={GE_AXIS_Y}
              x2={plotW} y2={GE_AXIS_Y}
              stroke="rgba(134,203,241,0.14)" strokeWidth="1.5"
            />
            <g clipPath={`url(#${clipIdGe})`}>
              {GLOBAL_EVENTS.map(ev => {
                const isActive = activeGlobal === ev.id;
                const cx       = xAt(evDate(ev));
                const dotR_ev  = ev.demo ? 5 : 3.5;
                const stemEnd  = GE_AXIS_Y + (ev.tier === 2 ? GE_STEM_2 : GE_STEM_1);
                const dotFill  = isActive ? 'rgba(134,203,241,1)' : 'rgba(134,203,241,0.8)';
                const lblFill  = isActive
                  ? '#D3ECFC'
                  : ev.demo
                    ? 'rgba(134,203,241,0.70)'
                    : 'rgba(134,203,241,0.45)';
                const stemFill = ev.demo
                  ? 'rgba(134,203,241,0.28)'
                  : 'rgba(134,203,241,0.18)';
                return (
                  <g
                    key={ev.id}
                    onClick={() => { if (!suppressClickRef.current) setActiveGlobal(isActive ? null : ev.id); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle cx={cx} cy={GE_AXIS_Y} r={dotR_ev} fill={dotFill} />
                    <line
                      x1={cx} y1={GE_AXIS_Y + dotR_ev}
                      x2={cx} y2={stemEnd}
                      stroke={stemFill} strokeWidth="1.5"
                    />
                    <text
                      x={cx} y={stemEnd + 3}
                      textAnchor="middle"
                      dominantBaseline="hanging"
                      fill={lblFill}
                      fontSize={isTablet ? 11 : 10}
                      fontWeight={ev.demo ? '500' : '600'}
                      fontFamily="'DM Sans', sans-serif"
                    >{ev.label}</text>
                  </g>
                );
              })}
            </g>
          </svg>

          {/* Info panel: shown when an event is tapped */}
          {activeGlobalEv && (
            <div className="td-ge-info">
              <div className="td-ge-info-head">
                <span className="td-ge-info-name">{activeGlobalEv.label}</span>
                <span className="td-ge-info-date">{activeGlobalEv.date}</span>
              </div>
              <p className="td-ge-info-note">{activeGlobalEv.note}</p>
            </div>
          )}

        </div>

        {/* "About these perspectives" link → opens PerspectiveInfoSheet popup */}
        <button className="td-persp-info-link" onClick={() => setSheetOpen(true)}>
          {t('persp.sheet.link')} →
        </button>

        {/* Insight text block - dynamic per thesis */}
        <div className="td-insight-block">
          {thesisData.insight.map((line, i) => (
            <p key={i} className="td-insight-line">
              {line.map((part, j) =>
                typeof part === 'string'
                  ? part
                  : <span key={j} style={{ color: colorOf(part.id) }}>{part.text}</span>
              )}
            </p>
          ))}
        </div>

        {/* Source note + global events disclaimer */}
        <p className="td-source-note">{t('td.sourceNote')}</p>
        <p className="td-global-note">Global events: context, not cause.</p>

        <div className="td-copyright">Prototype by Ferdinand Riedl · riedlsoftware.com</div>
      </div>

      <PerspectiveInfoSheet open={sheetOpen} onClose={() => setSheetOpen(false)} cohorts={COHORTS} />
    </div>
  );
}
